import { z } from 'zod';
import { PaymentType } from '../../constants/payment-type.js';
import { calendarDateOfUtcInstant } from '../../dates/index.js';
import { optionalClientGeneratedUuidString, optionalFormTrimmedString, optionalUuidString, parseStrictDate, requiredUuidString, } from '../zod-helpers/index.js';
import { lastAllowedPaymentDate } from './last-allowed-payment-date.js';
import { SALES_ORDER_PAYMENT_CONSTRAINTS } from './payment-constraints.js';
const { min: MIN_PAYMENT_DATE, maxDaysInFuture: MAX_PAYMENT_DATE_DAYS_IN_FUTURE } = SALES_ORDER_PAYMENT_CONSTRAINTS.paymentDate;
export const recordSalesOrderPaymentInputSchema = z.object({
    salesOrderId: requiredUuidString(`${SALES_ORDER_PAYMENT_CONSTRAINTS.salesOrder.label} is required.`, 'Invalid sales order selection.'),
    amount: z
        .number()
        .gt(0, `${SALES_ORDER_PAYMENT_CONSTRAINTS.amount.label} must be greater than zero.`)
        .max(SALES_ORDER_PAYMENT_CONSTRAINTS.amount.max, `${SALES_ORDER_PAYMENT_CONSTRAINTS.amount.label} must be ${SALES_ORDER_PAYMENT_CONSTRAINTS.amount.max.toLocaleString('en-US')} or less.`)
        // Rejects float drift such as 1000 * 1.005 by requiring the value to survive a toFixed(2) round trip.
        .refine((value) => Number(value.toFixed(2)) === value, `${SALES_ORDER_PAYMENT_CONSTRAINTS.amount.label} cannot have more than 2 decimal places.`),
    paymentMethodId: optionalUuidString(`Invalid ${SALES_ORDER_PAYMENT_CONSTRAINTS.paymentMethod.label.toLowerCase()} selection.`)
        .transform((value) => (value === '' ? undefined : value))
        .optional()
        .nullable(),
    // The column stays nullable for payments recorded elsewhere (LeafLink); recording one here needs a Type.
    paymentTypeId: z.enum(PaymentType, {
        error: (issue) => issue.input === undefined || issue.input === null
            ? SALES_ORDER_PAYMENT_CONSTRAINTS.paymentType.requiredMessage
            : `Invalid ${SALES_ORDER_PAYMENT_CONSTRAINTS.paymentType.label.toLowerCase()} selection.`,
    }),
    // Client-supplied retry key (v4 UUID), unique per recording user on customer_payment.
    idempotencyKey: optionalClientGeneratedUuidString('Invalid idempotency key.'),
    paymentDate: z
        .union([z.string(), z.date(), z.undefined(), z.null()], {
        error: `${SALES_ORDER_PAYMENT_CONSTRAINTS.paymentDate.label} must be a valid date.`,
    })
        .transform((value, ctx) => {
        if (value === undefined || value === null || value === '') {
            ctx.addIssue({
                code: 'custom',
                message: `${SALES_ORDER_PAYMENT_CONSTRAINTS.paymentDate.label} is required.`,
            });
            return z.NEVER;
        }
        // Strict UTC parsing, because a string without a trailing `Z` parses as local time and breaks the retry comparison.
        const parsed = parseStrictDate(value, ctx, `${SALES_ORDER_PAYMENT_CONSTRAINTS.paymentDate.label} must be a valid date.`);
        if (parsed === z.NEVER) {
            return z.NEVER;
        }
        if (parsed < MIN_PAYMENT_DATE) {
            ctx.addIssue({
                code: 'custom',
                message: `${SALES_ORDER_PAYMENT_CONSTRAINTS.paymentDate.label} cannot be before ${calendarDateOfUtcInstant(MIN_PAYMENT_DATE)}.`,
            });
            return z.NEVER;
        }
        // A picked payment day arrives at UTC midnight (the stand-in until F1 retypes the column),
        // so its UTC day is the day picked; a full timestamp is also read by its UTC day, which can
        // refuse one late on the last allowed Pacific day (2027-01-12T06:00Z is Jan 11 Pacific).
        if (calendarDateOfUtcInstant(parsed) > lastAllowedPaymentDate()) {
            ctx.addIssue({
                code: 'custom',
                message: `${SALES_ORDER_PAYMENT_CONSTRAINTS.paymentDate.label} cannot be more than ${MAX_PAYMENT_DATE_DAYS_IN_FUTURE} days in the future.`,
            });
            return z.NEVER;
        }
        return parsed;
    }),
    referenceNumber: optionalFormTrimmedString(SALES_ORDER_PAYMENT_CONSTRAINTS.referenceNumber.max).nullable(),
    notes: optionalFormTrimmedString(SALES_ORDER_PAYMENT_CONSTRAINTS.notes.max).nullable(),
});
//# sourceMappingURL=record-payment-input.js.map